import { getCompany } from 'helpers/getCompanyVariables';
import { isValidColor } from 'helpers/colors';
import { EVENT_COLORS, USER_EVENT_COLOR } from './constants';

// only Sunshine honors the per-record badge colors coming from Zoho; O'Neill
// keeps the rotating palette
const isSunshine = getCompany() === 'sunshine';

const FALLBACK_LIGHT_TEXT = '#FFFFFF';
const FALLBACK_DARK_TEXT = '#111827';
const PERCEIVED_BRIGHTNESS_THRESHOLD = 150;

const USER_EVENT_PALETTE = { ...USER_EVENT_COLOR, isCustom: false };

// the mini calendar resolves a color per event per day cell, so the same few
// (index, colors) combinations are asked for hundreds of times per render
const paletteCache = new Map();

function normalizeColor(value) {
  if (value == null) return '';
  const color = String(value).trim();
  return isValidColor(color) ? color : '';
}

function hexToRgb(value) {
  const hex = value.replace('#', '');
  if (hex.length === 3) {
    return hex.split('').map(c => parseInt(c + c, 16));
  }
  if (hex.length === 6) {
    return [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map(pair =>
      parseInt(pair, 16)
    );
  }
  return null;
}

// perceived brightness, only used to keep a badge readable over its own color
function isDarkColor(value) {
  const rgb = hexToRgb(value);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  return r * 0.299 + g * 0.587 + b * 0.114 < PERCEIVED_BRIGHTNESS_THRESHOLD;
}

function resolveBadgeTextColor(customText, bg, fallbackText) {
  if (!customText) return fallbackText;
  if (customText.toLowerCase() !== bg.toLowerCase()) return customText;
  // same color configured for text and background: the label would be invisible
  return isDarkColor(bg) ? FALLBACK_LIGHT_TEXT : FALLBACK_DARK_TEXT;
}

// findIndex hands back -1 when the event is no longer in the list it was
// numbered against, which would index the palette out of bounds
function toPaletteIndex(colorIdx) {
  const idx = Number.isInteger(colorIdx) ? colorIdx : 0;
  const length = EVENT_COLORS.length;
  return ((idx % length) + length) % length;
}

function buildImportantDateColor(paletteIdx, customText, customBg) {
  const fallback = EVENT_COLORS[paletteIdx];
  if (!customText && !customBg) return { ...fallback, isCustom: false };

  const bg = customBg || fallback.bg;
  return {
    bg,
    text: resolveBadgeTextColor(customText, bg, fallback.text),
    dot: customText || customBg,
    isCustom: true
  };
}

/**
 * resolves the palette of an important date, using the badge colors configured
 * in Zoho when they exist (Sunshine only) and the rotating palette otherwise
 * @param event - important date already mapped by importantDatesFactory
 * @param colorIdx - position of the event, picks the fallback palette entry
 */
export function getImportantDateColor(event, colorIdx = 0) {
  const paletteIdx = toPaletteIndex(colorIdx);
  const customText = isSunshine ? normalizeColor(event?.badgeColor) : '';
  const customBg = isSunshine ? normalizeColor(event?.badgeBgColor) : '';

  const cacheKey = `${paletteIdx}|${customText}|${customBg}`;
  const cached = paletteCache.get(cacheKey);
  if (cached) return cached;

  const resolved = buildImportantDateColor(paletteIdx, customText, customBg);
  paletteCache.set(cacheKey, resolved);
  return resolved;
}

/**
 * resolves the palette of any calendar item, so the agent's own events keep
 * their neutral color and important dates go through the Zoho-aware resolver
 * @param event - normalized calendar item carrying its `type`
 * @param colorIdx - position of the event, picks the fallback palette entry
 */
export function getCalendarItemColor(event, colorIdx = 0) {
  if (event?.type === 'calendarEvent') return USER_EVENT_PALETTE;
  return getImportantDateColor(event, colorIdx);
}
